import { ApplicationConfig, isDevMode, APP_INITIALIZER, inject } from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideTransloco } from '@jsverse/transloco';
import { Injectable } from '@angular/core';
import { TranslocoLoader, Translation } from '@jsverse/transloco';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { FIREBASE_OPTIONS } from '@angular/fire/compat';
import { AngularFireAuth } from '@angular/fire/compat/auth';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { EnvironmentService } from '../environments/environment-loader';
import { authInterceptor } from './interceptors/auth.interceptor';
import { AuthService } from './services/auth.service';
import { SharedApiService } from '../../../../libs/shared/src/lib/services/api.service';
import { AttachmentService } from '../../../../libs/shared/src/lib/services/attachment.service';
import { API_CONFIG } from '../../../../libs/shared/src/lib/tokens/api-config.token';
import { GOOGLE_MAPS_API_KEY } from '../../../../libs/shared/src/lib/services/google-maps.service';
import { ValidationTranslateLoader } from './shared/services/validation-translate-loader';

// Factory function to initialize EnvironmentService before app starts
const environmentFactory = (environmentService: EnvironmentService) => {
  return () => {
    // This runs before the app initializes
    return environmentService.environment;
  };
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withComponentInputBinding()),
    provideClientHydration(),
    provideAnimations(),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor])),
    // Environment Service
    EnvironmentService,
    {
      provide: APP_INITIALIZER,
      useFactory: environmentFactory,
      deps: [EnvironmentService],
      multi: true,
    },
    // API Config - using EnvironmentService for runtime values
    {
      provide: API_CONFIG,
      useFactory: (envService: EnvironmentService) => ({
        apiUrl: envService.environment.apiUrl,
        openApiUrl: envService.environment.openApiUrl
      }),
      deps: [EnvironmentService],
    },
    // Google Maps API Key - using EnvironmentService for runtime values
    {
      provide: GOOGLE_MAPS_API_KEY,
      useFactory: (envService: EnvironmentService) => envService.environment.googleMapsApiKey,
      deps: [EnvironmentService],
    },
    // Firebase providers - using EnvironmentService
    {
      provide: FIREBASE_OPTIONS,
      useFactory: (envService: EnvironmentService) => envService.environment.firebase,
      deps: [EnvironmentService],
    },
    provideFirebaseApp(() => {
      const envService = inject(EnvironmentService);
      return initializeApp(envService.environment.firebase);
    }),
    provideAuth(() => getAuth()),
    // Firebase Compat Auth for shared AuthInterceptorHelperService
    AngularFireAuth,
    // Services
    AuthService,
    SharedApiService,
    AttachmentService,
    provideTransloco({
      config: {
        availableLangs: ['en', 'sk', 'cz'],
        defaultLang: 'en',
        fallbackLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: {
          useFallbackTranslation: true,
        },
      },
      loader: ValidationTranslateLoader,
    }),
  ],
};
